<template>
  <b-modal :active.sync="isModalActive" :width="960" scroll="keep" @close="onModalClose">
    <div class="weekly-create-subactivity-modal">
      <div class="weekly-create-subactivity-modal__header">
        <h2 class="weekly-create-subactivity-modal__title">
          {{ $t('create_subactivity') }}
        </h2>
        <p v-if="parentActivityName" class="weekly-create-subactivity-modal__subtitle">
          {{ $t('activity') }}: {{ parentActivityName }}
        </p>
      </div>

      <div class="weekly-create-subactivity-modal__grid">
          <b-field :label="$t('activity_name')" class="weekly-create-subactivity-modal__field--full">
            <b-input v-model="form.name" maxlength="255" />
          </b-field>

          <b-field
            v-if="projectWeeks.length"
            :label="$t('week_of_the_year')"
            class="weekly-create-subactivity-modal__field--full"
          >
            <b-select
              v-model="selectedWeekIndex"
              expanded
              @input="onWeekSelected"
            >
              <option
                v-for="(week, index) in projectWeeks"
                :key="`week-${week.number}-${week.year}`"
                :value="index"
              >
                {{ week.label }} · {{ week.range }}
              </option>
            </b-select>
            <b-message
              v-if="weekOutsideParentRange"
              type="is-warning"
              size="is-small"
              class="weekly-create-subactivity-modal__week-warning"
            >
              {{ $t('weekly_subactivity_week_outside_parent_range', { range: parentDateRangeLabel }) }}
            </b-message>
          </b-field>

          <b-field :label="$t('planned_start_date')">
            <b-datepicker
              v-model="form.startDate"
              icon-pack="fas"
              append-to-body
              position="is-bottom-left"
              :min-date="parentDateMin"
              :max-date="startDateMax"
            />
          </b-field>

          <b-field :label="$t('planned_end_date')">
            <b-datepicker
              v-model="form.endDate"
              icon-pack="fas"
              append-to-body
              position="is-bottom-left"
              :min-date="endDateMin"
              :max-date="parentDateMax"
            />
          </b-field>
        </div>

        <div
          v-if="isLoadingBoqRoots || measurementRows.length"
          class="weekly-create-subactivity-modal__section weekly-create-subactivity-modal__section--primary"
        >
          <div class="weekly-create-subactivity-modal__section-title">
            <div class="weekly-create-subactivity-modal__section-title-main">
              <b-icon pack="fas" icon="link" size="is-small" class="mr-1" />
              {{ $t('inherited_primary_measurement_from_parent') }}
            </div>
            <span class="weekly-create-subactivity-modal__section-title-hint">
              {{ $t('inherited_primary_measurement_subtitle') }}
            </span>
            <span class="weekly-create-subactivity-modal__section-title-count">
              {{ $t('assigned_measurements_count', { count: assignedMeasurementsCount }) }}
            </span>
          </div>

          <div class="weekly-create-subactivity-modal__table-wrap overflow-x-auto">
            <div v-if="isLoadingBoqRoots" class="weekly-create-subactivity-modal__loading">
              {{ $t('loading') }}...
            </div>
            <table
              v-else
              class="weekly-create-subactivity-modal__table w-full text-xs border-collapse min-w-max"
            >
              <thead>
                <tr>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--use">
                    {{ $t('use') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--cap">
                    {{ $t('chapter_abbr') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--code">
                    {{ $t('code') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--name">
                    {{ $t('name') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--numeric">
                    {{ $t('reserve') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--numeric">
                    {{ $t('others_short') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--numeric">
                    {{ $t('available') }}
                  </th>
                  <th class="weekly-create-subactivity-modal__th weekly-create-subactivity-modal__th--numeric">
                    {{ $t('quantity') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in measurementRows"
                  :key="item.boq_root_id"
                  class="weekly-create-subactivity-modal__row"
                >
                <!-- check box to use the measurement -->
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--use">
                    <b-checkbox v-model="item.use" />
                  </td>
                  <!-- chapter code -->
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--cap">
                    <span class="weekly-create-subactivity-modal__cap-badge">{{ item.chapter_code || '—' }}</span>
                  </td>
                  <!-- code -->
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--code">
                    <span class="weekly-create-subactivity-modal__code">{{ item.code }}</span>
                  </td>
                  <!-- name -->
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--name">
                    <div class="weekly-create-subactivity-modal__name-title">{{ item.name }}</div>
                    <span v-if="item.is_primary" class="weekly-create-subactivity-modal__primary-badge">
                      ★ {{ $t('primary') }}
                    </span>
                    <div
                      v-if="item.activity_quantity != null"
                      class="weekly-create-subactivity-modal__name-meta"
                    >
                      {{ $t('measurement_total_unit_global_assigned', {
                        quantity: formatQuantity(item.activity_quantity),
                        unit: item.unit,
                        percent: formatPercent(item.assigned_percent),
                      }) }}
                    </div>
                  </td>
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--numeric">
                    <span class="weekly-create-subactivity-modal__value--bold">
                      {{ formatQuantity(item.reserve) }}
                    </span>
                    <span class="weekly-create-subactivity-modal__unit-suffix">{{ item.unit }}</span>
                  </td>
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--numeric">
                    {{ formatQuantity(item.other_subs) }}
                  </td>
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--numeric">
                    <span :class="availableClass(item)">
                      {{ formatQuantity(item.available) }}
                    </span>
                    <span class="weekly-create-subactivity-modal__unit-suffix">{{ item.unit }}</span>
                  </td>
                  <td class="weekly-create-subactivity-modal__td weekly-create-subactivity-modal__td--quantity">
                    <div v-if="item.use" class="weekly-create-subactivity-modal__quantity-group">
                      <input
                        type="number"
                        class="weekly-create-subactivity-modal__quantity-input"
                        :min="0"
                        :max="quantityMax(item)"
                        step="1"
                        :value="item.quantity"
                        @input="onQuantityInput(item, $event)"
                      />
                      <span class="weekly-create-subactivity-modal__unit">{{ item.unit }}</span>
                      <button
                        type="button"
                        class="weekly-create-subactivity-modal__todo-btn"
                        @click="setQuantityAll(item)"
                      >
                        {{ $t('all').toUpperCase() }}
                      </button>
                    </div>
                    <span v-else class="weekly-create-subactivity-modal__quantity-empty">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="weekly-create-subactivity-modal__warning">
            <b-icon pack="fas" icon="exclamation-triangle" size="is-small" class="mr-1" />
            {{ $t('weekly_subactivity_measurement_hint') }}
          </p>
        </div>

        <div
          class="weekly-create-subactivity-modal__section weekly-create-subactivity-modal__section--manual"
          :class="{
            'weekly-create-subactivity-modal__section--manual-active': form.addManualMeasurement,
            'weekly-create-subactivity-modal__section--manual-invalid': showManualMeasurementWarning,
          }"
        >
          <div class="weekly-create-subactivity-modal__manual-header">
            <b-checkbox
              v-model="form.addManualMeasurement"
              @input="onManualMeasurementToggled"
            >
              <span class="weekly-create-subactivity-modal__manual-title">
                {{ $t('additional_manual_measurement') }}
              </span>
            </b-checkbox>
            <span class="weekly-create-subactivity-modal__manual-header-hint">
              {{ $t('additional_manual_measurement_hint') }}
            </span>
          </div>

          <div v-if="form.addManualMeasurement" class="weekly-create-subactivity-modal__manual-body">
            <div class="weekly-create-subactivity-modal__manual-grid">
              <b-field :label="$t('measurement_name')">
                <b-select
                  v-model="form.manualBoqRootId"
                  expanded
                  :placeholder="$t('select_manual_measurement')"
                  @input="onManualResourceSelected"
                >
                  <option
                    v-for="item in manualMeasurementRows"
                    :key="item.boq_root_id"
                    :value="item.boq_root_id"
                  >
                    {{ item.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field :label="$t('unit')">
                <b-input
                  v-model="form.manualUnit"
                  :placeholder="$t('measurement_unit_placeholder')"
                  readonly
                />
              </b-field>

              <b-field :label="$t('quantity')">
                <input
                  type="number"
                  class="weekly-create-subactivity-modal__quantity-input weekly-create-subactivity-modal__quantity-input--manual"
                  min="0"
                  step="1"
                  :value="form.manualQuantity"
                  @input="onManualQuantityInput"
                />
              </b-field>
            </div>

            <p
              v-if="showManualMeasurementWarning"
              class="weekly-create-subactivity-modal__manual-warning"
            >
              <b-icon pack="fas" icon="exclamation-triangle" size="is-small" class="mr-1" />
              {{ $t('weekly_manual_measurement_validation') }}
            </p>
          </div>
        </div>

        <div class="weekly-create-subactivity-modal__grid weekly-create-subactivity-modal__grid--personnel">
          <b-field :label="$t('supervisor')">
            <b-input
              v-model="form.supervisor"
              :placeholder="$t('select_supervisor')"
              disabled
            />
          </b-field>

          <b-field :label="$t('person_in_charge')" icon="search" icon-pack="fas">
            <b-autocomplete
              v-model="managerSearch"
              :data="managerResults"
              :placeholder="$t('search')"
              field="fullname"
              open-on-focus
              append-to-body
              dropdown-position="bottom"
              class="weekly-create-subactivity-modal__manager-autocomplete"
              @typing="onManagerTyping"
              @select="onManagerSelected"
            >
              <template v-slot="props">
                <div class="text-xs">
                  <span class="font-semibold">{{ props.option.fullname }}</span>
                  <span v-if="props.option.email" class="text-grey-dark"> ({{ props.option.email }})</span>
                </div>
              </template>
              <template #empty>{{ $t('no_results') }}</template>
            </b-autocomplete>
          </b-field>

          <b-field :label="$t('staffing_people_count')">
            <b-numberinput
              v-model="form.peopleCount"
              :min="1"
              controls-position="compact"
              size="is-small"
              icon-pack="fas"
            />
            <p class="weekly-create-subactivity-modal__staffing-hint">
              {{ $t('staffing_hours_hint') }}
            </p>
          </b-field>
        </div>

      <div class="weekly-create-subactivity-modal__footer">
        <button
          type="button"
          class="weekly-create-subactivity-modal__cancel-btn"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="weekly-create-subactivity-modal__save-btn"
          :disabled="isSaving"
          @click="submit"
        >
          {{ $t('create_subactivity') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { buildProjectWeeks, findWeekIndexForDate } from './weeklyProjectWeeks.js'

function buildManagerLabel(manager) {
  if (!manager) {
    return null
  }
  if (manager.fullname) {
    return manager.fullname
  }
  const parts = [manager.name, manager.surname].filter(Boolean)
  return parts.length ? parts.join(' ') : null
}

function buildManagerSearchRequest(projectId, query) {
  return {
    url: '/api/v2/activity/users/search',
    params: {
      only_internal: 1,
      p: projectId,
      q: query,
    },
  }
}

function mapManagerSearchResults(users) {
  if (!Array.isArray(users)) {
    return []
  }
  return users.map((user) => ({
    ...user,
    fullname: buildManagerLabel(user) || `${user.name || ''} ${user.surname || ''}`.trim(),
  }))
}

function buildBoqRootsGroupedRequest(projectId, activityId) {
  return {
    url: `/api/v2/project/${projectId}/activity/${activityId}/boq-roots/grouped`,
  }
}

function resolveBoqRootUnit(boqRoot) {
  if (!boqRoot || !boqRoot.measure_unit) {
    return ''
  }
  return boqRoot.measure_unit.code || boqRoot.measure_unit.name || ''
}

function resolveBoqRootParentCode(boqRoot) {
  if (!boqRoot || !boqRoot.parent) {
    return null
  }
  if (boqRoot.parent.code) {
    return boqRoot.parent.code
  }
  if (boqRoot.parent.chapter && boqRoot.parent.chapter.code) {
    return boqRoot.parent.chapter.code
  }
  return null
}

function resolveApiIsPrimary(value) {
  return value === true || value === 1 || value === '1'
}

function mapBoqRootToMeasurementRow(boqRoot) {
  const totalQuantity = parseFloat(boqRoot.total_quantity)
  const activityQuantity = parseFloat(boqRoot.activity_quantity)
  const assignedQuantity = parseFloat(boqRoot.assigned_quantity)
  const available = boqRoot.available_units != null ? parseFloat(boqRoot.available_units) : null

  const hasTotal = !Number.isNaN(totalQuantity)
  const hasActivityQuantity = !Number.isNaN(activityQuantity)
  const hasAssignedQuantity = !Number.isNaN(assignedQuantity)
  const others = (hasTotal && hasActivityQuantity)
    ? Math.max(0, totalQuantity - activityQuantity)
    : null

  const assignedPercent = (hasActivityQuantity && activityQuantity > 0 && hasAssignedQuantity)
    ? Math.round((assignedQuantity / activityQuantity) * 100)
    : null

  return {
    boq_root_id: boqRoot.id,
    chapter_code: resolveBoqRootParentCode(boqRoot),
    code: boqRoot.code || '',
    name: boqRoot.name || '',
    unit: resolveBoqRootUnit(boqRoot),
    total_quantity: hasTotal ? totalQuantity : null,
    activity_quantity: hasActivityQuantity ? activityQuantity : null,
    assigned_quantity: hasAssignedQuantity ? assignedQuantity : null,
    assigned_percent: assignedPercent,
    reserve: hasActivityQuantity ? activityQuantity : null,
    other_subs: others,
    available: available != null && !Number.isNaN(available) ? available : null,
    is_primary: resolveApiIsPrimary(boqRoot.is_primary),
    use: false,
    quantity: 0,
  }
}

function parseApiDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.toDate() : null
}

function formatPayloadDate(value) {
  if (!value) {
    return null
  }
  return moment(value).format('YYYY-MM-DD')
}

function buildBoqAssignmentPayload(rows) {
  if (!Array.isArray(rows)) {
    return []
  }

  return rows
    .filter((item) => item.use && parseInt(item.quantity, 10) > 0)
    .map((item) => ({
      boq_root_id: item.boq_root_id,
      total_units: parseInt(item.quantity, 10),
    }))
}

function buildBoqManualPayload(form) {
  if (!form.addManualMeasurement || !form.manualBoqRootId) {
    return []
  }

  const quantity = parseInt(form.manualQuantity, 10)
  if (Number.isNaN(quantity) || quantity <= 0) {
    return []
  }

  return [{
    boq_root_id: form.manualBoqRootId,
    total_units: quantity,
  }]
}

export default {
  name: 'weekly-create-subactivity-modal',

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isModalActive: false,
      isSaving: false,
      parentActivityId: null,
      parentActivityName: null,
      parentLocationId: null,
      parentDateMin: null,
      parentDateMax: null,
      measurementRows: [],
      manualMeasurementRows: [],
      isLoadingBoqRoots: false,
      managerSearch: '',
      managerResults: [],
      selectedManager: null,
      selectedWeekIndex: 0,
      weekOutsideParentRange: false,
      form: {
        name: '',
        startDate: null,
        endDate: null,
        supervisor: '',
        peopleCount: 3,
        addManualMeasurement: false,
        manualBoqRootId: null,
        manualUnit: '',
        manualQuantity: 0,
      },
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    projectWeeks() {
      if (!this.project) {
        return []
      }

      return buildProjectWeeks(
        this.project.current_start_date,
        this.project.current_end_date
      )
    },

    assignedMeasurementsCount() {
      return this.measurementRows.filter((item) => (
        item.use && parseFloat(item.quantity) > 0
      )).length
    },

    showManualMeasurementWarning() {
      if (!this.form.addManualMeasurement) {
        return false
      }
      const qty = parseInt(this.form.manualQuantity, 10)
      return !(qty > 0)
    },

    startDateMax() {
      if (this.form.endDate) {
        return this.form.endDate
      }
      return this.parentDateMax
    },

    endDateMin() {
      if (this.form.startDate) {
        return this.form.startDate
      }
      return this.parentDateMin
    },

    parentDateRangeLabel() {
      if (!this.parentDateMin || !this.parentDateMax) {
        return ''
      }
      const format = 'DD/MM/YYYY'
      return `${moment(this.parentDateMin).format(format)} – ${moment(this.parentDateMax).format(format)}`
    },
  },

  methods: {
    show({ parentActivity = null } = {}) {
      this.parentActivityId = (parentActivity && parentActivity.id) ? parentActivity.id : null
      this.parentActivityName = (parentActivity && parentActivity.name)
        ? parentActivity.name
        : null
      this.parentDateMin = parseApiDate(parentActivity && parentActivity.current_start_date)
        || moment('2026-01-01').toDate()
      this.parentDateMax = parseApiDate(parentActivity && parentActivity.current_end_date)
        || moment('2026-12-31').toDate()
      this.resetForm()
      this.isModalActive = true
      this.loadParentActivityContext()
      this.fetchBoqRootsGrouped()
    },

    async loadParentActivityContext() {
      this.parentLocationId = null
      if (!this.parentActivityId) {
        return
      }

      try {
        const { data } = await axios.get(`/api/v2/activity/${this.parentActivityId}`)
        if (data && data.success && data.data) {
          this.parentLocationId = data.data.location_id || null
        }
      } catch (error) {
        console.error('[weekly-create-subactivity-modal] load parent activity error:', error)
      }
    },

    async fetchBoqRootsGrouped() {
      if (!this.effectiveProjectId || !this.parentActivityId) {
        this.measurementRows = []
        this.manualMeasurementRows = []
        return
      }

      const { url } = buildBoqRootsGroupedRequest(this.effectiveProjectId, this.parentActivityId)
      this.isLoadingBoqRoots = true

      try {
        const { data } = await axios.get(url)
        if (!data || !data.success) {
          this.measurementRows = []
          this.manualMeasurementRows = []
          return
        }

        const imported = Array.isArray(data.imported) ? data.imported : []
        const manual = Array.isArray(data.manual) ? data.manual : []

        this.measurementRows = imported.map((boqRoot) => mapBoqRootToMeasurementRow(boqRoot))
        this.manualMeasurementRows = manual.map((boqRoot) => mapBoqRootToMeasurementRow(boqRoot))
      } catch (error) {
        this.measurementRows = []
        this.manualMeasurementRows = []
        console.error('[weekly-create-subactivity-modal] boq-roots/grouped error:', error)
      } finally {
        this.isLoadingBoqRoots = false
      }
    },

    resetForm() {
      this.selectedWeekIndex = this.resolveDefaultWeekIndex()
      const week = this.projectWeeks[this.selectedWeekIndex]
      const weekStart = week ? week.start.format('YYYY-MM-DD') : this.weekStartDate
      const weekEnd = week ? week.end.format('YYYY-MM-DD') : this.weekEndDate

      this.form = {
        name: '',
        startDate: this.clampToParent(weekStart),
        endDate: this.clampToParent(weekEnd, true),
        supervisor: '',
        peopleCount: 3,
        addManualMeasurement: false,
        manualBoqRootId: null,
        manualUnit: '',
        manualQuantity: 0,
      }
      this.managerSearch = ''
      this.managerResults = []
      this.selectedManager = null
      this.measurementRows = []
      this.manualMeasurementRows = []
      this.updateWeekOutsideParentWarning()
    },

    clampToParent(dateString, useEnd = false) {
      const parsed = parseApiDate(dateString)
      if (!parsed) {
        return useEnd ? this.parentDateMax : this.parentDateMin
      }
      if (this.parentDateMin && parsed < this.parentDateMin) {
        return this.parentDateMin
      }
      if (this.parentDateMax && parsed > this.parentDateMax) {
        return this.parentDateMax
      }
      return parsed
    },

    resolveDefaultWeekIndex() {
      if (!this.projectWeeks.length) {
        return 0
      }

      if (this.weekStartDate) {
        return findWeekIndexForDate(this.projectWeeks, this.weekStartDate)
      }

      return findWeekIndexForDate(this.projectWeeks, moment())
    },

    onWeekSelected(index) {
      this.selectedWeekIndex = index
      this.applySelectedWeekDates()
      this.updateWeekOutsideParentWarning()
    },

    isWeekOutsideParentRange(week) {
      if (!week || !this.parentDateMin || !this.parentDateMax) {
        return false
      }

      const weekStart = week.start.clone().startOf('day')
      const weekEnd = week.end.clone().startOf('day')
      const parentMin = moment(this.parentDateMin).startOf('day')
      const parentMax = moment(this.parentDateMax).startOf('day')

      return weekStart.isBefore(parentMin, 'day') || weekEnd.isAfter(parentMax, 'day')
    },

    updateWeekOutsideParentWarning() {
      const week = this.projectWeeks[this.selectedWeekIndex]
      this.weekOutsideParentRange = this.isWeekOutsideParentRange(week)
    },

    applySelectedWeekDates() {
      const week = this.projectWeeks[this.selectedWeekIndex]
      if (!week) {
        this.weekOutsideParentRange = false
        return
      }

      this.form.startDate = this.clampToParent(week.start.format('YYYY-MM-DD'))
      this.form.endDate = this.clampToParent(week.end.format('YYYY-MM-DD'), true)
    },

    async onManagerTyping(value) {
      const query = value != null ? String(value).trim() : ''
      if (!query || query.length < 3) {
        this.managerResults = []
        return
      }
      if (!this.effectiveProjectId) {
        return
      }
      const { url, params } = buildManagerSearchRequest(this.effectiveProjectId, query)
      try {
        const { data } = await axios.get(url, { params })
        const users = (data && data.success && Array.isArray(data.users))
          ? data.users
          : (data && Array.isArray(data.users) ? data.users : [])
        this.managerResults = mapManagerSearchResults(users)
      } catch (error) {
        this.managerResults = []
      }
    },

    onManagerSelected(user) {
      if (!user) {
        return
      }
      this.selectedManager = user
      this.managerSearch = user.fullname || buildManagerLabel(user) || ''
    },

    formatQuantity(value) {
      const num = parseFloat(value)
      if (Number.isNaN(num)) {
        return '—'
      }
      return num.toFixed(2).replace('.', ',')
    },

    formatPercent(value) {
      const num = parseFloat(value)
      if (Number.isNaN(num)) {
        return '—'
      }
      return `${Math.round(num)}`
    },

    availableClass(item) {
      const available = parseFloat(item.available)
      if (!Number.isNaN(available) && available <= 0) {
        return 'weekly-create-subactivity-modal__value--danger'
      }
      return 'weekly-create-subactivity-modal__value--success'
    },

    onManualMeasurementToggled(enabled) {
      if (!enabled) {
        this.form.manualBoqRootId = null
        this.form.manualUnit = ''
        this.form.manualQuantity = 0
      }
    },

    onManualResourceSelected() {
      const row = this.manualMeasurementRows.find(
        (item) => String(item.boq_root_id) === String(this.form.manualBoqRootId)
      )
      this.form.manualUnit = row ? row.unit : ''
    },

    onManualQuantityInput(event) {
      const raw = event && event.target ? event.target.value : ''
      if (raw === '') {
        this.form.manualQuantity = 0
        return
      }

      let value = parseInt(raw, 10)
      if (Number.isNaN(value) || value < 0) {
        value = 0
      }

      this.form.manualQuantity = value
      if (event && event.target) {
        event.target.value = value
      }
    },

    setQuantityAll(item) {
      if (item.available == null) {
        return
      }
      item.quantity = this.quantityMax(item)
    },

    quantityMax(item) {
      if (item.available == null) {
        return undefined
      }
      const max = Math.floor(parseFloat(item.available))
      return Number.isNaN(max) ? undefined : max
    },

    onQuantityInput(item, event) {
      const raw = event && event.target ? event.target.value : ''
      if (raw === '') {
        item.quantity = 0
        return
      }

      let value = parseInt(raw, 10)
      if (Number.isNaN(value) || value < 0) {
        value = 0
      }

      const max = this.quantityMax(item)
      if (max != null && value > max) {
        value = max
      }

      item.quantity = value
      if (event && event.target) {
        event.target.value = value
      }
    },

    resolveManagerUserId() {
      if (!this.selectedManager) {
        return null
      }
      return this.selectedManager.user_id || this.selectedManager.id || null
    },

    validateSubactivityForm() {
      const name = String(this.form.name || '').trim()
      if (!name) {
        this.$notify.error('field_required')
        return false
      }
      if (!this.form.startDate || !this.form.endDate) {
        this.$notify.error('please_complete_required_fields')
        return false
      }
      if (!this.resolveManagerUserId()) {
        this.$notify.error('error_no_users_in_list', { list: this.$t('activity_manager') })
        return false
      }
      if (!this.parentLocationId) {
        this.$notify.error('please_complete_required_fields')
        return false
      }
      if (!this.effectiveProjectId || !this.parentActivityId) {
        this.$notify.error('error_creating_subactivity')
        return false
      }
      return true
    },

    buildSubactivityCreatePayload() {
      const startDate = formatPayloadDate(this.form.startDate)
      const endDate = formatPayloadDate(this.form.endDate)
      const managerUserId = this.resolveManagerUserId()

      return {
        project_id: this.effectiveProjectId,
        activity_id: this.parentActivityId,
        name: String(this.form.name || '').trim(),
        location_id: this.parentLocationId,
        description: '',
        moment_scheduled_start_date: startDate,
        moment_scheduled_end_date: endDate,
        moment_current_start_date: startDate,
        moment_current_end_date: endDate,
        moment_real_start_date: startDate,
        moment_real_end_date: endDate,
        manager_user: managerUserId,
        participants: [],
        collaborators: [],
        show_on_devices: true,
        in_planning: false,
        weight: null,
        people: parseInt(this.form.peopleCount, 10) || null,
        boq_imported: buildBoqAssignmentPayload(this.measurementRows),
        boq_manual: buildBoqManualPayload(this.form),
      }
    },

    async submit() {
      if (this.isSaving) {
        return
      }
      if (!this.validateSubactivityForm()) {
        return
      }

      this.isSaving = true
      try {
        const payload = this.buildSubactivityCreatePayload()
        const { data } = await axios.post('/api/v2/activity', payload)
        if (data && data.success) {
          this.$notify.success('success_creating_subactivity')
          this.$emit('created', {
            activity_id: data.activity_id,
            parent_activity_id: this.parentActivityId,
          })
          this.close()
        } else {
          this.$notify.error('error_creating_subactivity')
        }
      } catch (error) {
        console.error('[weekly-create-subactivity-modal] create subactivity error:', error)
        this.$notify.error('error_creating_subactivity')
      } finally {
        this.isSaving = false
      }
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.parentActivityId = null
      this.parentActivityName = null
      this.parentLocationId = null
      this.isSaving = false
    },
  },
}
</script>

<style scoped>
.weekly-create-subactivity-modal {
  padding: 1rem 1.25rem 1.25rem;
  background-color: #fff;
}

.weekly-create-subactivity-modal__header {
  margin-bottom: 1rem;
}

.weekly-create-subactivity-modal__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #3b66bc;
}

.weekly-create-subactivity-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #6b7280;
}

.weekly-create-subactivity-modal__loading {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.weekly-create-subactivity-modal__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.weekly-create-subactivity-modal__field--full {
  grid-column: 1 / -1;
}

.weekly-create-subactivity-modal__week-warning {
  margin-top: 0.5rem;
}

.weekly-create-subactivity-modal__grid--personnel {
  margin-top: 1rem;
  margin-bottom: 0;
}

.weekly-create-subactivity-modal__section {
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.weekly-create-subactivity-modal__section--primary {
  border-color: #f59e0b;
  background-color: #fffbeb;
}

.weekly-create-subactivity-modal__section-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.75rem;
  padding: 0.5rem 0.75rem;
  background-color: #fef3c7;
  border-bottom: 1px solid #fcd34d;
  font-size: 0.6875rem;
  color: #92400e;
}

.weekly-create-subactivity-modal__section-title-main {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
}

.weekly-create-subactivity-modal__section-title-hint {
  flex: 1 1 12rem;
  font-size: 0.625rem;
  font-weight: 400;
  line-height: 1.35;
}

.weekly-create-subactivity-modal__section-title-count {
  margin-left: auto;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.weekly-create-subactivity-modal__th {
  padding: 0.4375rem 0.625rem;
  font-weight: 700;
  font-size: 0.625rem;
  text-transform: uppercase;
  color: #2c426c;
  background-color: #e8eef7;
  border: 1px solid #c5d4eb;
  white-space: nowrap;
}

.weekly-create-subactivity-modal__th--use,
.weekly-create-subactivity-modal__th--cap,
.weekly-create-subactivity-modal__th--code,
.weekly-create-subactivity-modal__th--name {
  text-align: left;
}

.weekly-create-subactivity-modal__th--numeric,
.weekly-create-subactivity-modal__th--quantity {
  text-align: right;
}

.weekly-create-subactivity-modal__th--name {
  min-width: 14rem;
}

.weekly-create-subactivity-modal__td {
  padding: 0.5rem 0.625rem;
  border: 1px solid #e5e7eb;
  vertical-align: middle;
  background-color: #fff;
}

.weekly-create-subactivity-modal__td--use {
  text-align: center;
  width: 2.5rem;
}

.weekly-create-subactivity-modal__td--cap {
  text-align: center;
  width: 3rem;
}

.weekly-create-subactivity-modal__td--code {
  white-space: nowrap;
}

.weekly-create-subactivity-modal__td--name {
  min-width: 14rem;
}

.weekly-create-subactivity-modal__td--numeric,
.weekly-create-subactivity-modal__td--quantity {
  text-align: right;
  white-space: nowrap;
}

.weekly-create-subactivity-modal__cap-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background-color: #5b3f8c;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
}

.weekly-create-subactivity-modal__code {
  font-weight: 600;
  color: #3b66bc;
  font-size: 0.6875rem;
}

.weekly-create-subactivity-modal__name-title {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.weekly-create-subactivity-modal__name-meta {
  margin-top: 0.25rem;
  font-size: 0.625rem;
  color: #6b7280;
  line-height: 1.35;
}

.weekly-create-subactivity-modal__primary-badge {
  display: inline-block;
  margin-top: 0.25rem;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background-color: #f59e0b;
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-create-subactivity-modal__value--bold {
  font-weight: 700;
  color: #1f2937;
}

.weekly-create-subactivity-modal__unit-suffix {
  margin-left: 0.125rem;
  font-size: 0.625rem;
  color: #9ca3af;
}

.weekly-create-subactivity-modal__value--success {
  font-weight: 700;
  color: #166534;
}

.weekly-create-subactivity-modal__value--danger {
  font-weight: 700;
  color: #b91c1c;
}

.weekly-create-subactivity-modal__quantity-group {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

.weekly-create-subactivity-modal__quantity-input {
  width: 5.5rem;
  height: 1.75rem;
  padding: 0.125rem 0.375rem;
  border: 1px solid #dbdbdb;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  text-align: right;
  background-color: #fff;
}

.weekly-create-subactivity-modal__quantity-input:focus {
  outline: none;
  border-color: #3b66bc;
  box-shadow: 0 0 0 1px #3b66bc;
}

.weekly-create-subactivity-modal__quantity-input::-webkit-outer-spin-button,
.weekly-create-subactivity-modal__quantity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.weekly-create-subactivity-modal__quantity-input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.weekly-create-subactivity-modal__unit {
  font-size: 0.625rem;
  color: #6b7280;
}

.weekly-create-subactivity-modal__todo-btn {
  padding: 0.1875rem 0.5rem;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #3b66bc;
  font-size: 0.5625rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-create-subactivity-modal__todo-btn:hover {
  background-color: #eff6ff;
}

.weekly-create-subactivity-modal__quantity-empty {
  color: #9ca3af;
}

.weekly-create-subactivity-modal__warning {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.6875rem;
  color: #92400e;
  background-color: #fffbeb;
  border-top: 1px solid #fcd34d;
  line-height: 1.4;
}

.weekly-create-subactivity-modal__manual {
  margin-bottom: 0.75rem;
}

.weekly-create-subactivity-modal__section--manual {
  border-color: #d1d5db;
  background-color: #fff;
}

.weekly-create-subactivity-modal__section--manual-active {
  border-color: #86efac;
  background-color: #f0fdf4;
}

.weekly-create-subactivity-modal__section--manual-invalid {
  border-color: #fca5a5;
  background-color: #fef2f2;
}

.weekly-create-subactivity-modal__manual-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.75rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.weekly-create-subactivity-modal__section--manual-active .weekly-create-subactivity-modal__manual-header {
  border-bottom-color: #bbf7d0;
}

.weekly-create-subactivity-modal__section--manual-invalid .weekly-create-subactivity-modal__manual-header {
  border-bottom-color: #fecaca;
}

.weekly-create-subactivity-modal__manual-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #166534;
}

.weekly-create-subactivity-modal__manual-header-hint {
  flex: 1 1 12rem;
  font-size: 0.625rem;
  color: #6b7280;
  line-height: 1.35;
}

.weekly-create-subactivity-modal__manual-body {
  padding: 0.75rem;
}

.weekly-create-subactivity-modal__manual-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0.75rem 1rem;
}

.weekly-create-subactivity-modal__manual-warning {
  display: flex;
  align-items: flex-start;
  margin: 0.75rem 0 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.6875rem;
  color: #b91c1c;
  background-color: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 0.25rem;
  line-height: 1.4;
}

.weekly-create-subactivity-modal__quantity-input--manual {
  width: 100%;
}

.weekly-create-subactivity-modal__manual-hint {
  margin: 0.25rem 0 0 1.75rem;
  font-size: 0.6875rem;
  color: #6b7280;
}

.weekly-create-subactivity-modal__staffing-hint {
  margin: 0.25rem 0 0;
  font-size: 0.6875rem;
  color: #6b7280;
}

.weekly-create-subactivity-modal__manager-autocomplete {
  width: 100%;
}

.weekly-create-subactivity-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.weekly-create-subactivity-modal__cancel-btn,
.weekly-create-subactivity-modal__save-btn {
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-create-subactivity-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
}

.weekly-create-subactivity-modal__save-btn {
  border: none;
  background-color: #3b66bc;
  color: #fff;
}

.weekly-create-subactivity-modal__save-btn:disabled,
.weekly-create-subactivity-modal__cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>

<style>
/* append-to-body: Buefy fija z-index 99 inline; .modal usa z-index 100 */
body > .autocomplete.control,
body > .datepicker.control {
  z-index: 120 !important;
}
</style>
